import { createSlice } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';

/**
 * Holds the server's public certificate fetched from `getcertificatebyorigin`.
 *
 * Why redux: the certificate is needed by (almost) every API call to wrap the
 * per-request AES session key. Keeping it here means getcertificate is called
 * once per page load instead of once per API call, and because the store is
 * persisted to localStorage by redux-persist, a reload or a client-side route
 * change rehydrates it with no network call at all.
 *
 * The certificate is public data (it is the public half of the server's key
 * pair), so there is no confidentiality concern in persisting it.
 */
const initialState = {
  // Base64-encoded DER X.509 certificate, exactly as the API returns it.
  certificate: null,
  // Server-supplied version string, e.g. "1.4". Useful for debugging rotations.
  certificateVersion: null,
  // Epoch ms of the last successful fetch, for diagnostics only.
  fetchedAt: null,
};

const certificateSlice = createSlice({
  name: 'certificate',
  initialState,
  reducers: {
    setCertificate: (state, action) => {
      const { certificate, certificateVersion } = action.payload;
      state.certificate = certificate;
      state.certificateVersion = certificateVersion || null;
      state.fetchedAt = new Date().getTime();
    },
    clearCertificate: (state) => {
      state.certificate = null;
      state.certificateVersion = null;
      state.fetchedAt = null;
    },
  },
});

export const { setCertificate, clearCertificate } = certificateSlice.actions;

export default certificateSlice.reducer;

export function useCertificateStore() {
  const dispatch = useDispatch();
  const certificate = useSelector((state) => state.certificate.certificate);
  const certificateVersion = useSelector(
    (state) => state.certificate.certificateVersion
  );
  const fetchedAt = useSelector((state) => state.certificate.fetchedAt);

  return {
    certificate,
    certificateVersion,
    fetchedAt,

    setCertificate: (payload) => dispatch(setCertificate(payload)),
    clearCertificate: () => dispatch(clearCertificate()),
  };
}
